// src/components/Workspace/CollaboratorNavBar.js - Fixed workspace name display
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../../contexts/UserContext';

function CollaboratorNavBar() {
  const { 
    isCollaborator, 
    collaboratorWorkspaces, 
    activeBusinessProfileId,
    switchCollaboratorWorkspace,
    isWorkspaceSwitching
  } = useUserContext();
  
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();

  // Get the active owner UID from localStorage
  const activeOwnerUid = localStorage.getItem('active_workspace_owner_uid');

  // Find the active workspace information using both profileId and ownerUid
  useEffect(() => {
    if (isCollaborator && collaboratorWorkspaces?.length > 0 && activeBusinessProfileId) {
      // Find workspace that matches both ID and owner (if available)
      let workspace;
      
      if (activeOwnerUid) {
        workspace = collaboratorWorkspaces.find(
          w => w.id === activeBusinessProfileId && w.ownerUid === activeOwnerUid
        );
      }
      
      // Fallback to just matching ID if necessary
      if (!workspace) {
        workspace = collaboratorWorkspaces.find(w => w.id === activeBusinessProfileId);
      }
      
      if (workspace) {
        setCurrentWorkspace(workspace);
      }
    }
  }, [isCollaborator, collaboratorWorkspaces, activeBusinessProfileId, activeOwnerUid]);

  // If not a collaborator, only one workspace, or no active workspace, don't show this component
  if (!isCollaborator || collaboratorWorkspaces?.length <= 1 || !currentWorkspace) {
    return null;
  }

  // Handle workspace switching with owner UID
  const handleWorkspaceChange = async (workspace) => {
    setIsDropdownOpen(false);
    
    // Check if this is already the active workspace
    if (workspace.id === activeBusinessProfileId && workspace.ownerUid === activeOwnerUid) {
      return;
    }
    
    console.log(`Switching to workspace: ${workspace.profileName || workspace.workspaceName} (ID: ${workspace.id}, Owner: ${workspace.ownerUid})`);
    const success = await switchCollaboratorWorkspace(workspace.id, workspace.ownerUid);
    
    if (success) {
      console.log(`Successfully switched to workspace: ${workspace.profileName || workspace.workspaceName}`);
    } else {
      console.error(`Failed to switch to workspace: ${workspace.profileName || workspace.workspaceName}`);
    }
  };

  return (
    <div className="bg-blue-50 border-b border-blue-200 py-2 px-4 mb-4">
      <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between">
        <div className="flex items-center mb-2 sm:mb-0">
          <div className="bg-blue-600 text-white p-2 rounded-full mr-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <div>
            <span className="text-sm text-blue-800">Collaborator Access:</span>
            <h3 className="font-medium text-blue-900 flex items-center">
              {/* Clean workspace name */}
              {currentWorkspace.profileName || currentWorkspace.workspaceName}
              {/* Separate badge element */}
              <span className="text-xs ml-2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full inline-block">
                ID:{currentWorkspace.ownerUid.substring(0, 4)}
              </span>
            </h3>
          </div>
        </div>
        
        <div className="flex space-x-2">
          {/* Workspace Dropdown Selector */}
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="px-3 py-1 bg-white text-blue-700 border border-blue-300 rounded-md text-sm hover:bg-blue-50 flex items-center"
              disabled={isWorkspaceSwitching}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="max-w-[120px] truncate">
                {isWorkspaceSwitching ? 'Switching...' : 'Switch Workspace'}
              </span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-700">Your Workspaces</p>
                  <p className="text-xs text-gray-500">Select a workspace to access</p>
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  {collaboratorWorkspaces.map((workspace) => {
                    // Determine if this is the active workspace (using both id and ownerUid)
                    const isActive = workspace.id === activeBusinessProfileId && 
                                    workspace.ownerUid === activeOwnerUid;
                    
                    // Get the clean workspace name
                    const workspaceName = workspace.profileName || workspace.workspaceName || 'Unnamed Workspace';
                    
                    return (
                      <button
                        key={`${workspace.ownerUid}-${workspace.id}`}
                        onClick={() => handleWorkspaceChange(workspace)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 ${
                          isActive ? 'bg-blue-50 font-medium' : ''
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <div className="flex-1">
                          <div className="font-medium">
                            {/* Just the workspace name, no concatenation */}
                            {workspaceName}
                            {/* Add owner indicator as a separate element with proper spacing */}
                            <span className="ml-2 text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full inline-block">
                              ID:{workspace.ownerUid.substring(0, 4)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            Role: {workspace.role === 'owner' ? 'Owner' : 'Collaborator'}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={() => navigate('/dashboard')}
            className="px-3 py-1 bg-white text-blue-700 border border-blue-300 rounded-md text-sm hover:bg-blue-50 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Dashboard
          </button>
          
          <button
            onClick={() => navigate('/workspace')}
            className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
            Manage Workspace
          </button>
        </div>
      </div>
    </div>
  );
}

export default CollaboratorNavBar;